import type { ShapeTextCaret } from '@/lib/shapes/types/shape-text-caret'

/** Vertical distance from `y` to the row band `[yTop, yTop + rowHeight)`; 0 when inside it. */
export function rowBandDistance(y: number, yTop: number, rowHeight: number): number {
  if (y < yTop) return yTop - y
  const bottom = yTop + rowHeight

  return y >= bottom ? y - bottom : 0
}

/**
 * Text offset for a point in shape-local document pixels: **row first, then character** — the order
 * every text editor uses.
 *
 * The previous implementation minimised `dx² + dy²` across every caret in the frame, weighting
 * horizontal and vertical distance equally. That is only safe when each row spans the full frame
 * width. Shape text rows do not: `getShapeTextFlow` slices a concave outline into *disjoint*
 * segments per row, so a star's row can be two 15px slivers with a 113px gap between them. A click
 * whose x lands far from any caret on its own row then loses to a caret one or two rows away, which
 * is how a click on line 3 of a star burst inserted at the end of the last line (WMPCM-8161).
 *
 * Bands are compared by distance to the band *interval*, not to its centre: `getShapeTextFlow` skips
 * rows whose height-aware segments collapse, so `yTop` values are not contiguous and a click inside
 * a skipped gap must resolve to the nearer surviving band.
 */
export function caretIndexAtShapeLocalPoint(
  carets: ShapeTextCaret[],
  localX: number,
  localY: number
): number {
  if (carets.length === 0) return 0

  // ── Pass 1: choose the row band.
  let bandYTop = carets[0].yTop
  let bandDistance = Number.POSITIVE_INFINITY

  const considerBand = (yTop: number, rowHeight: number) => {
    const distance = rowBandDistance(localY, yTop, rowHeight)

    // Equidistant between two bands (a click in the middle of a skipped-row gap) resolves *downward*,
    // matching every editor's behaviour for a click in the whitespace below a line: the offset lands
    // at the start of the row below rather than the end of the row above.
    if (distance < bandDistance || (distance === bandDistance && yTop > bandYTop)) {
      bandDistance = distance
      bandYTop = yTop
    }
  }

  for (const caret of carets) {
    considerBand(caret.yTop, caret.rowHeight)
    // A slot-boundary caret can belong to two bands — see the pass-2 comment. (For a within-row
    // segment jump this is the band it already contributed, which is harmless.)
    if (caret.nextSlotYTop !== undefined) considerBand(caret.nextSlotYTop, caret.rowHeight)
  }

  // ── Pass 2: nearest caret x within that band. Strict `<` keeps the lowest index on a tie, which
  // is what makes overflowed text — every offset past `consumedLength` duplicates the last caret —
  // resolve to the first of those duplicates, i.e. the end of the text that is actually laid out.
  let bestIdx = 0
  let bestDx = Number.POSITIVE_INFINITY

  const considerCaret = (index: number, x: number) => {
    const dx = Math.abs(x - localX)

    if (dx < bestDx) {
      bestDx = dx
      bestIdx = index
    }
  }

  for (let i = 0; i < carets.length; i++) {
    const caret = carets[i]

    // A slot-boundary caret keeps `x`/`yTop` on the slot it *ends* (so `computeSelectionRects` can
    // close that slot's rect) while `nextSlotX`/`nextSlotYTop` hold the same offset's position as the
    // first character of the slot the flow continues into. Offering both means a click before the
    // first glyph of a wrapped row — or of a second segment on a pinched row — resolves to that
    // offset instead of to the character after it.
    //
    // Offering both is also what makes the gap between two segments resolve sanely without any
    // segment bookkeeping here: a click anywhere in the gap is nearest to one of this one caret's
    // two positions, so either way it returns the boundary offset.
    if (caret.yTop === bandYTop) considerCaret(i, caret.x)
    if (caret.nextSlotYTop === bandYTop && caret.nextSlotX !== undefined) {
      considerCaret(i, caret.nextSlotX)
    }
  }

  return bestIdx
}

export function computeSelectionRects(
  carets: ShapeTextCaret[],
  selStart: number,
  selEnd: number
): Array<{ x: number; y: number; width: number; height: number }> {
  if (selStart >= selEnd || carets.length < 2) return []

  const rects: Array<{ x: number; y: number; width: number; height: number }> = []
  const rowMap = new Map<number, { x1: number; x2: number; height: number }>()
  // Per-row thickness taken from the FULL caret array rather than from whichever caret happened to
  // seed the row below. The wrap-boundary branch seeds a new row from `c0`, and `c0` sits on the
  // PREVIOUS row by that branch's own convention — so the seeded `height` is the previous row's
  // thickness, and the `if (row)` branch only ever revises `x1`/`x2`, never `height`. A row entered
  // by a natural wrap therefore kept the wrong height forever: invisible while every row was the
  // same size, wrong the moment they differ.
  //
  // `Math.max` rather than last-write-wins: `applyPerLineLeadingToFlow` keeps
  // `carets[consumedLength]`, which can belong to a row the overflow break dropped — that caret
  // keeps its pre-restack `yTop`/`rowHeight` and could otherwise hand a live row a stale value.
  //
  // The `?? height` fallback at the emit loop is consequently unreachable, not defensive. `rowMap`
  // is keyed on `c0.yTop` and `c0.nextSlotYTop`; the former always has an entry here, and the latter
  // can only miss one when it points at a dropped row. That requires a wrap boundary at some index
  // `i` whose continuation row was dropped — but then every character from `i` on was dropped too,
  // so `consumedLength <= i` and `carets[i]` is the array's last element, which `i + 1 >=
  // carets.length` breaks before ever using as `c0`.
  const thicknessByYTop = new Map<number, number>()

  for (const caret of carets) {
    thicknessByYTop.set(caret.yTop, Math.max(thicknessByYTop.get(caret.yTop) ?? 0, caret.rowHeight))
  }

  for (let i = selStart; i < selEnd; i++) {
    if (i + 1 >= carets.length) break
    const c0 = carets[i]
    const c1 = carets[i + 1]

    if (c0.yTop !== c1.yTop) {
      // Natural-wrap boundary: anchor x1 of the new row using the start position stored on c0.
      // c0 stays on the old row so its x still closes the old row's x2 correctly. A within-row
      // segment jump does not reach here (c0.yTop === c1.yTop) and does not need to: its highlight
      // stays one rect spanning the gap, which the shape's own clip path hides.
      if (c0.nextSlotX !== undefined && c0.nextSlotYTop !== undefined) {
        const newRow = rowMap.get(c0.nextSlotYTop)

        if (newRow) {
          newRow.x1 = Math.min(newRow.x1, c0.nextSlotX)
        } else {
          rowMap.set(c0.nextSlotYTop, {
            x1: c0.nextSlotX,
            x2: c0.nextSlotX,
            height: c0.rowHeight,
          })
        }
      }

      continue
    }

    const row = rowMap.get(c0.yTop)

    if (row) {
      row.x1 = Math.min(row.x1, c0.x)
      row.x2 = Math.max(row.x2, c1.x)
    } else {
      rowMap.set(c0.yTop, { x1: c0.x, x2: c1.x, height: c0.rowHeight })
    }
  }

  for (const [yTop, { x1, x2, height }] of rowMap) {
    // `height` is the fallback only — see `thicknessByYTop` above for why the seeded value can be
    // the previous row's.
    if (x2 > x1) {
      rects.push({ x: x1, y: yTop, width: x2 - x1, height: thicknessByYTop.get(yTop) ?? height })
    }
  }

  return rects
}
